/**
 * Someone talking.
 *
 * A box along the bottom rather than a full-screen panel: you are still stood
 * in the world, and a conversation that hides the farm makes the farm feel
 * like a menu you left. It also leaves the bag and the hearts visible, which
 * matters when somebody is telling you what they will pay for a turnip.
 *
 * Options are buttons rather than a cursor and a confirm. On a phone the
 * cursor is the thumb.
 */

export interface DialogueOption {
  readonly label: string;
  readonly onChoose: () => void;
}

export class DialoguePanel {
  private readonly root: HTMLDivElement;
  private readonly speaker: HTMLSpanElement;
  private readonly text: HTMLParagraphElement;
  private readonly choices: HTMLDivElement;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'dialogue';
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');

    this.speaker = document.createElement('span');
    this.speaker.className = 'dialogue-speaker';

    this.text = document.createElement('p');
    this.text.className = 'dialogue-text';

    this.choices = document.createElement('div');
    this.choices.className = 'dialogue-choices';

    this.root.append(this.speaker, this.text, this.choices);
    container.appendChild(this.root);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  show(speaker: string, text: string, options: readonly DialogueOption[]): void {
    this.speaker.textContent = speaker;
    this.text.textContent = text;
    this.choices.replaceChildren();

    for (const option of options) {
      const button = document.createElement('button');
      button.className = 'dialogue-choice';
      button.type = 'button';
      button.textContent = option.label;
      button.addEventListener('click', () => {
        option.onChoose();
      });
      this.choices.appendChild(button);
    }

    this.root.hidden = false;
  }

  close(): void {
    this.root.hidden = true;
    this.choices.replaceChildren();
  }

  destroy(): void {
    this.root.remove();
  }
}
